import Route from '@ember/routing/route';
import { isPresent } from '@ember/utils';
import { service } from '@ember/service';
import PledgeValidations from '../../../../validations/pledge';
import lookupValidator from 'ember-changeset-validations';
import Changeset from 'ember-changeset';
import {
  pledgeOneTimeFrequency,
  calculateEndDateMonthsForOneTimePledge,
  canEditPledge,
} from 'donor-elf-web/lib/pledge-utils';
import AbilitiesService from 'donor-elf-web/services/abilities';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import NavbarSettingsService from 'donor-elf-web/services/navbar-settings';
import Store from '@ember-data/store';
import RouterService from '@ember/routing/router-service';
import PledgeModel from 'donor-elf-web/models/pledge';
import LineItemModel from 'donor-elf-web/models/line-item';
import ContactModel from 'donor-elf-web/models/contact';
import moment from 'moment';

export interface PledgeEditRouteModel {
  record: PledgeModel;
  changeset: any;
  gifts: LineItemModel[];
  contact?: ContactModel;
  fromURL?: string;
}

export default class PledgeEditRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare contactsCache: ContactsCache;
  @service declare navbarSettings: NavbarSettingsService;
  @service declare router: RouterService;
  @service declare store: Store;

  queryParams = {
    contactId: {},
  };

  beforeModel(transition: any) {
    if (
      this.abilities.noAbility('edit_pledges') &&
      this.abilities.noAbility('edit_manual_pledges') &&
      this.abilities.noAbility('edit_system_pledge_end_date')
    ) {
      transition.abort();
    }
  }

  async model(params: any): Promise<PledgeEditRouteModel> {
    let fromURL: string | undefined = this.router.currentURL;
    if (fromURL.match(/pledges\/\d+$/)) {
      fromURL = undefined;
    }
    let record: PledgeModel;
    if (params.pledgeId === 'add') {
      record = await this.store.createRecord('pledge', {
        contactId: params.contactId,
      });
      this.navbarSettings.title = 'Add Pledge';
    } else {
      record = await this.store.findRecord('pledge', params.pledgeId);
      this.navbarSettings.title = 'Edit Pledge';
    }

    let contact: ContactModel | undefined;
    let contactLineItems: LineItemModel[] | undefined = undefined;
    if (isPresent(record.contactId)) {
      contactLineItems = (
        await this.store.query('line-item', {
          filter: { contact_id: record.contactId },
        })
      ).slice();
      contact = this.contactsCache.contactsById[record.contactId!];
    }

    let changeset = new Changeset(
      record,
      lookupValidator(PledgeValidations),
      PledgeValidations,
    ) as any;

    let gifts = contactLineItems?.filter((x) => x.amount > 0) || [];
    if (record.isNew && gifts.length > 0) {
      let firstGift = gifts[gifts.length - 1];
      changeset.set('startDate', firstGift.transDate);
      changeset.set('amount', firstGift.amount);
      if (gifts.length >= 3) {
        let monthsApart = moment(gifts[0].transDate).diff(
          gifts[2].transDate,
          'months',
          true,
        );
        if (monthsApart <= 4) {
          changeset.set('frequency', 1);
        }
      }
    }

    changeset.set('endDateMonths', '12');
    if (changeset.get('frequency') === pledgeOneTimeFrequency) {
      let endDateMonths = calculateEndDateMonthsForOneTimePledge({
        startDate: changeset.get('startDate'),
        endDate: changeset.get('endDate'),
      });
      if (endDateMonths) {
        changeset.set('endDateMonths', endDateMonths.toString());
      }
    }

    return {
      record,
      changeset,
      gifts,
      contact,
      fromURL,
    };
  }

  afterModel(model: PledgeEditRouteModel, transition: any) {
    if (!canEditPledge(model.record, this.abilities)) {
      transition.abort();
    }
  }
}
